import React from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import QuizFunnel from './components/QuizFunnel';
import OAuthCallback from './components/OAuthCallback';
import RoatanQuizFunnel from './components/RoatanQuizFunnel';
import { defaultAgents } from './data/industryTemplates';
import { getAgentConfig, getCompleteAgentConfig } from './utils/agentConfig';
import './index.css';

// Create Material-UI theme
const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
    background: {
      default: '#f5f5f5',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h4: {
      fontWeight: 600,
    },
    h6: {
      fontWeight: 600,
    },
  },
});

function StatusScreen({ title, message }) {
  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: '#f5f7fb',
        px: 3,
      }}
    >
      <Box sx={{ textAlign: 'center', maxWidth: 460 }}>
        <CircularProgress sx={{ mb: 3 }} />
        <Typography variant="h5" gutterBottom>{title}</Typography>
        <Typography color="text.secondary">{message}</Typography>
      </Box>
    </Box>
  );
}

function MessageScreen({ title, message }) {
  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: '#f5f7fb',
        px: 3,
      }}
    >
      <Box sx={{ textAlign: 'center', maxWidth: 520 }}>
        <Typography variant="h5" gutterBottom>{title}</Typography>
        <Typography color="text.secondary">{message}</Typography>
      </Box>
    </Box>
  );
}

function QuizEntryPoint({ agentId, forceRoatan = false }) {
  const [agentConfig, setAgentConfig] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');

  React.useEffect(() => {
    let isMounted = true;

    async function loadAgent() {
      try {
        setLoading(true);
        setError('');

        if (forceRoatan) {
          setAgentConfig(getCompleteAgentConfig(defaultAgents.robbiehin));
          return;
        }

        if (!agentId) {
          setAgentConfig(getCompleteAgentConfig(defaultAgents.erika));
          return;
        }

        const agent = defaultAgents[agentId] || await getAgentConfig(agentId);

        if (!isMounted) return;

        if (!agent) {
          setError(`We couldn't find a quiz for agent "${agentId}".`);
          setAgentConfig(null);
          return;
        }

        setAgentConfig(getCompleteAgentConfig(agent));
      } catch (loadError) {
        if (!isMounted) return;
        console.error('Failed to load quiz configuration:', loadError);
        setError('Unable to load this quiz right now. Please try again later.');
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    }

    loadAgent();

    return () => {
      isMounted = false;
    };
  }, [agentId, forceRoatan]);

  if (loading) {
    return <StatusScreen title="Loading quiz" message="Preparing the funnel and agent details..." />;
  }

  if (error) {
    return <MessageScreen title="Quiz unavailable" message={error} />;
  }

  if (agentConfig?.customFunnel === 'roatan' || agentConfig?.id === 'robbiehin') {
    return <RoatanQuizFunnel agentConfig={agentConfig} />;
  }

  return <QuizFunnel agentConfig={agentConfig} />;
}

function App() {
  const pathname = window.location.pathname;
  const searchParams = new URLSearchParams(window.location.search);
  const agentId = searchParams.get('agent');
  const isCallbackRoute = pathname === '/auth/callback';
  const isRoatanRoute = pathname.startsWith('/quiz/robbiehin');
  const isQuizRoute = pathname === '/' || pathname.startsWith('/quiz') || Boolean(agentId);
  const shouldForceRoatan = isRoatanRoute || pathname === '/' || !agentId;

  let content = <QuizEntryPoint forceRoatan={shouldForceRoatan} agentId={agentId || 'robbiehin'} />;

  if (isCallbackRoute) {
    content = <OAuthCallback />;
  } else if (isRoatanRoute) {
    content = <QuizEntryPoint forceRoatan />;
  } else if (isQuizRoute) {
    content = <QuizEntryPoint forceRoatan={shouldForceRoatan} agentId={agentId || 'robbiehin'} />;
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {content}
    </ThemeProvider>
  );
}

export default App;